import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { QueryParams } from '@aid/shared/types/interfaces/query-params.interface';
import { tap } from 'rxjs/operators';
import { PaginationService } from '@aid/shared/services/pagination.service';

export abstract class CrudPaginationService<
  T extends { id?: number | null }
> extends PaginationService<T> {
  protected values = new BehaviorSubject<T[] | null>([]);
  protected queryParams = new BehaviorSubject<QueryParams>(
    this.getInitialQueryParams()
  );
  protected loading = new BehaviorSubject<boolean | null>(null);
  protected count = new BehaviorSubject<number | null>(null);
  protected hasNext = new BehaviorSubject<boolean | null>(null);
  protected loadMore = false;
  protected cursor = new BehaviorSubject<string | null>(null);

  protected constructor(protected _http: HttpClient, protected cache = false) {
    super(_http, cache);
  }

  get(valueId: number): Observable<T> {
    return this._http
      .get<T>(`${this.url}/${valueId}`)
      .pipe(tap(() => this.loading.next(false)));
  }

  add(value: T): Observable<T> {
    return this.addRequest(value).pipe(tap(result => this.addValue(result)));
  }

  edit(value: T): Observable<T> {
    return this._http
      .patch<T>(`${this.url}/${value.id}`, value)
      .pipe(tap(result => this.editValue(result)));
  }

  delete(id: number) {
    return this._http.delete(`${this.url}/${id}`).pipe(
      tap(() => this.deleteValue(id)),
      tap(() => this.getNextPageAfterDelete())
    );
  }

  protected addRequest(value: T): Observable<T> {
    return this._http.post<T>(`${this.url}`, value);
  }

  protected addValue(value: T) {
    const values = this.values.value;
    if (!values) {
      return;
    }
    this.values.next([value, ...values]);
  }

  protected editValue(value: T) {
    const values = this.values.value;
    if (!values) {
      return;
    }

    const index = values.findIndex(item => item.id === value.id);
    values[index] = value;

    this.values.next([...values]);
  }

  /**
   * Get next page after delete
   * This method is used only if the cursor pagination is used
   */
  protected getNextPageAfterDelete() {
    const values = this.values.value;
    const pageSize = this.queryParamsValue.pageSize;
    if (!values || !this.hasNext.value || !this.cursor.value) {
      return;
    }
    if (pageSize !== undefined && values.length < pageSize) {
      this.loadNextPage();
    }
  }

  protected deleteValue(valueId: number) {
    const values = this.values.value;
    if (!values) {
      return;
    }

    const index = values.findIndex(item => item.id === valueId);
    values.splice(index, 1);
    this.values.next([...values]);
  }
}
